import React, { Suspense, lazy, useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import MyNavbar from './components/layout/MyNavbar';
import Whatsapp from './components/common/WhatsApp';
import { SearchProvider, useSearch } from './context/SearchContext';
import { CarritoProvider } from './context/CarritoContext';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/layout/ScrollToTop';
import Loader from './components/layout/Loader';
import PageLoader from './components/layout/PageLoader';
import ProductDetailModal from './components/product/ProductDetailModal';
import Login from './pages/admin/Login';
import Admin from './pages/admin/Admin';
import AdminProductos from './pages/admin/AdminProductos';
import AdminEditarProducto from './pages/admin/AdminEditarProducto';
import AdminPedidos from './pages/admin/AdminPedidos';
import AdminCategorias from './pages/admin/AdminCategorias';
import AdminLayout from './components/layout/AdminLayout';

const Home = lazy(() => import('./pages/public/Home'));
const Fundas = lazy(() => import('./pages/public/Fundas'));
const Accesorios = lazy(() => import('./pages/public/Accesorios'));
const Vidrios = lazy(() => import('./pages/public/Vidrios'));
const Carrito = lazy(() => import('./pages/public/Carrito'));
const CompraExitosa = lazy(() => import('./pages/public/CompraExitosa'));
const CompraError = lazy(() => import('./pages/public/CompraError'));
const CompraPendiente = lazy(() => import('./pages/public/CompraPendiente'));
const PedidoRegistrado = lazy(() => import('./pages/public/PedidoRegistrado'));
const TerminosYCondiciones = lazy(() => import('./pages/public/TerminosYCondiciones'));
const Contact = lazy(() => import('./pages/public/Contact'));

function SearchProductHost() {
  const { productoDetalle, cerrarProducto } = useSearch();
  return (
    <ProductDetailModal
      producto={productoDetalle}
      show={!!productoDetalle}
      onHide={cerrarProducto}
    />
  );
}

function AppContent() {
  const location = useLocation();
  const isLogin = location.pathname === '/login';
  const isAdmin = location.pathname.startsWith('/admin');
  const isPedidosRoot = location.pathname === '/pedidos';
  const isPedidoConfirm =
    location.pathname === '/pedido-registrado' || location.pathname === '/compra-exitosa';
  const hideChrome = isLogin || isAdmin || isPedidosRoot;
  const [bootLoading, setBootLoading] = useState(true);
  const handleBootDone = useCallback(() => setBootLoading(false), []);

  useEffect(() => {
    // Remove all route classes first
    document.body.classList.remove('login-route');
    document.body.classList.remove('chromeless-route');
    document.body.classList.remove('pedido-confirm-route');
    if (hideChrome) {
      document.body.classList.add('chromeless-route');
      if (isLogin) {
        document.body.classList.add('login-route');
      }
    }
    if (isPedidoConfirm) {
      document.body.classList.add('pedido-confirm-route');
    }
    return () => {
      document.body.classList.remove('login-route');
      document.body.classList.remove('chromeless-route');
      document.body.classList.remove('pedido-confirm-route');
    };
  }, [hideChrome, isLogin, isPedidoConfirm]);

  useEffect(() => {
    document.body.style.overflow = bootLoading ? 'hidden' : '';
    if (!bootLoading) {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [bootLoading]);

  return (
    <div className="App">
      {bootLoading && <PageLoader onDone={handleBootDone} />}
      <a href="#contenido-principal" className="skip-link">
        Saltar al contenido
      </a>
      <ScrollToTop />
      {!hideChrome && (
        <header className="site-chrome">
          <MyNavbar />
        </header>
      )}
      {!hideChrome && <Whatsapp />}
      <SearchProductHost />
      <div id="contenido-principal" tabIndex={-1}>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/cajas" element={<Fundas />} />
            {/* Rutas legacy ocultas del nav — se mantienen vivas por compatibilidad */}
            <Route path="/embalaje" element={<Accesorios />} />
            <Route path="/autoarmables" element={<Vidrios />} />
            <Route path="/fundas" element={<Navigate to="/cajas" replace />} />
            <Route path="/accesorios" element={<Navigate to="/embalaje" replace />} />
            <Route path="/vidrios" element={<Navigate to="/autoarmables" replace />} />
            <Route path="/carrito" element={<Carrito />} />
            <Route path="/compra-exitosa" element={<CompraExitosa />} />
            <Route path="/pedido-registrado" element={<PedidoRegistrado />} />
            <Route path="/checkout/failure" element={<CompraError />} />
            <Route path="/checkout/pending" element={<CompraPendiente />} />
            <Route path="/terminos-y-condiciones" element={<TerminosYCondiciones />} />
            <Route path="/contacto" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Admin />} />
              <Route path="productos" element={<AdminProductos />} />
              <Route path="productos/nuevo" element={<AdminEditarProducto />} />
              <Route path="productos/editar/:id" element={<AdminEditarProducto />} />
              <Route path="categorias" element={<AdminCategorias />} />
            </Route>
            <Route path="/pedidos" element={<AdminLayout />}>
              <Route index element={<AdminPedidos />} />
            </Route>
          </Routes>
        </Suspense>
      </div>
      {!hideChrome && (
        <footer>
          <Footer />
        </footer>
      )}
    </div>
  );
}

function App() {
  return (
    <SearchProvider>
      <CarritoProvider>
        <Router>
          <AppContent />
        </Router>
      </CarritoProvider>
    </SearchProvider>
  );
}

export default App;
